import React, { useState, useEffect } from "react";
import { Popover, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { useKnowledgeBase } from "../../context/KnowledgeBaseContext";
import { BrainBankOption } from "../../hooks/useBrainBanks";
import { CheckIcon, LoaderCircle, Search, DatabaseZap, X } from "lucide-react";
import { useUser } from "../../context/UserContext";
import Tooltip from "../Tooltip/Tooltip";

// The chat-side BrainBank picker. Same shape as HatInput, the VA picker it sits
// next to: a popover with a search box and a single-select list. No favourites
// toggle here -- BrainBanks have no favourites endpoint, and the VA one is built
// on /hat/{id}/favorite.
//
// Unlike HatInput it carries a clear row: clicking the selected row again also
// clears the selection, but nothing on screen says so. It reads as an action
// rather than as the navbar dropdown's "No BrainBank" option, because that one
// is a real value of a Listbox -- here there is nothing to clear until something
// is picked, so the row only appears then.
// panelAlign "start" opens the list from the button's left edge, for a
// button near the left of a dialog, where the centred default would be cut off.
const KnowledgeBaseInput = ({ title = "BrainBank", panelAlign = "center" }: { title?: string; panelAlign?: "center" | "start" }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const {
    knowledgeBasesList = [],
    fetchKnowledgeBasesList,
    selectedKnowledgeBase,
    setSelectedKnowledgeBase,
    loading,
  } = useKnowledgeBase();

  const { userId } = useUser();

  const loadKnowledgeBaseList = async () => {
    try {
      await fetchKnowledgeBasesList();
    } catch (error) {
      console.error("Error loading BrainBanks:", error);
    }
  };

  useEffect(() => {
    loadKnowledgeBaseList();
  }, [fetchKnowledgeBasesList, userId]);

  const getIconState = () => {
    if (selectedKnowledgeBase) {
      return true;
    }
    return isHovered;
  };

  const handleClearClick = (event: React.MouseEvent) => {
    event.preventDefault();
    setSelectedKnowledgeBase(null);
  };

  const handleKnowledgeBaseClick = (event: React.MouseEvent, knowledgeBase: BrainBankOption) => {
    event.preventDefault();
    if (selectedKnowledgeBase?.id === knowledgeBase.id) {
      setSelectedKnowledgeBase(null);
    } else {
      setSelectedKnowledgeBase(knowledgeBase);
    }
  };

  const filteredKnowledgeBases = React.useMemo(() => {
    if (!searchQuery) {
      return knowledgeBasesList;
    }

    return knowledgeBasesList.filter(
      (knowledgeBase) =>
        knowledgeBase.name && knowledgeBase.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [knowledgeBasesList, searchQuery]);

  return (
    <Popover className="relative">
      <Tooltip label={title} side="top">
        <Popover.Button
          className="bg-surface-2 text-muted hover:bg-surface-3 hover:text-primary focus:outline-none transition-colors duration-200 p-2 rounded-full relative"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          // Reload on every open: a BrainBank created since this chat box mounted
          // (Settings, or the meetings Send to BrainBank dialog) is otherwise missing.
          onClick={loadKnowledgeBaseList}
        >
          <span className="flex items-center">
            <DatabaseZap
              size={18}
              strokeWidth={1.75}
              className={getIconState() ? "text-violet-600" : ""}
            />
          </span>
        </Popover.Button>
      </Tooltip>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-150"
        enterFrom="opacity-0 translate-y-1"
        enterTo="opacity-100 translate-y-0"
        leave="transition ease-in duration-150"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 translate-y-1"
      >
        <Popover.Panel className={`absolute z-10 bg-surface-overlay text-primary ring-1 ring-border-default shadow-lg rounded-lg p-2 mt-3 w-[320px] max-h-[400px] overflow-hidden ${panelAlign === "start" ? "left-0" : "-right-21 -left-20 sm:-right-20"}`}>
          {loading && knowledgeBasesList.length === 0 ? (
            <div className="flex justify-center items-center p-4">
              <LoaderCircle className="animate-spin text-muted" size={17} />
            </div>
          ) : (
            <div className="space-y-2">
              {/* Search input */}
              <div className="relative flex-shrink-0">
                <input
                  type="search"
                  placeholder="Search BrainBanks..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-3 py-1.5 pr-8 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500"
                />
                <Search size={14} className="absolute right-2.5 top-2 text-gray-400" />
              </div>

              {/* BrainBank list */}
              <div className="max-h-[280px] overflow-y-auto pb-2">
                {selectedKnowledgeBase && (
                  <button
                    onClick={handleClearClick}
                    className="flex items-center justify-between w-full p-2 mb-1 rounded-lg text-muted hover:bg-surface-2 hover:text-primary transition border-b border-border-default"
                  >
                    <span className="text-sm font-medium whitespace-nowrap text-left flex-grow pr-2">
                      Clear selection
                    </span>
                    <div className="flex items-center space-x-2">
                      <X className="w-4 h-4" />
                    </div>
                  </button>
                )}

                {filteredKnowledgeBases.length > 0 ? (
                  filteredKnowledgeBases.map((knowledgeBase) => (
                    <button
                      key={knowledgeBase.id}
                      onClick={(event) => handleKnowledgeBaseClick(event, knowledgeBase)}
                      className="flex items-center justify-between w-full p-2 rounded-lg hover:bg-surface-2 transition group"
                    >
                      <span
                        className={`text-sm font-medium whitespace-nowrap text-left flex-grow pr-2 truncate
                          ${selectedKnowledgeBase?.id === knowledgeBase.id ? "text-primary font-semibold" : "text-primary"}`}
                      >
                        {knowledgeBase.name || 'Unnamed BrainBank'}
                      </span>
                      <div className="flex items-center space-x-2">
                        {selectedKnowledgeBase?.id === knowledgeBase.id && (
                          <CheckIcon className="text-green-500 w-4 h-4" />
                        )}
                      </div>
                    </button>
                  ))
                ) : (
                  <p className="text-sm text-gray-500 text-center py-4">
                    {searchQuery
                      ? "No BrainBanks match your search"
                      : "No BrainBanks available"}
                  </p>
                )}
              </div>
            </div>
          )}
        </Popover.Panel>
      </Transition>
    </Popover>
  );
};

export default KnowledgeBaseInput;
